/**
 * Memory Service Contracts
 *
 * gRPC service definitions for conversation memory management:
 * - Semantic search across conversation history
 * - Importance scoring and prioritization
 * - Message summarization for context reduction
 * - Embedding generation and storage
 */

import { z } from 'zod';

/**
 * Message retrieval result with relevance scoring
 */
export const MessageRetrievalSchema = z.object({
  messageId: z.string().uuid(),
  conversationId: z.string().uuid(),
  content: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  importance: z.number().min(0).max(1).optional(),
  relevanceScore: z.number().min(0).max(1),
  createdAt: z.date(),
});

export type MessageRetrieval = z.infer<typeof MessageRetrievalSchema>;

/**
 * Search request for semantic similarity
 */
export const SemanticSearchRequestSchema = z.object({
  conversationId: z.string().uuid(),
  businessId: z.string().uuid(),
  query: z.string().min(1).max(32000),
  topK: z.number().int().min(1).max(50).default(10),
  minRelevance: z.number().min(0).max(1).default(0.5),
  includeImportance: z.boolean().default(true),
});

export type SemanticSearchRequest = z.infer<typeof SemanticSearchRequestSchema>;

/**
 * Search result containing relevant messages
 */
export const SemanticSearchResultSchema = z.object({
  results: z.array(MessageRetrievalSchema),
  totalMatched: z.number().int().nonnegative(),
  searchDuration: z.number().int().nonnegative(),
});

export type SemanticSearchResult = z.infer<typeof SemanticSearchResultSchema>;

/**
 * Summarization request
 */
export const SummarizationRequestSchema = z.object({
  conversationId: z.string().uuid(),
  businessId: z.string().uuid(),
  messageIds: z.array(z.string().uuid()).min(1),
  maxLength: z.number().int().min(50).max(1000).default(500),
});

export type SummarizationRequest = z.infer<typeof SummarizationRequestSchema>;

/**
 * Summarization result
 */
export const SummarizationResultSchema = z.object({
  conversationId: z.string().uuid(),
  summary: z.string(),
  keyPoints: z.array(z.string()),
  importance: z.number().min(0).max(1),
  messageCount: z.number().int().positive(),
  summarizationDuration: z.number().int().nonnegative(),
});

export type SummarizationResult = z.infer<typeof SummarizationResultSchema>;

/**
 * Embedding generation request
 */
export const EmbeddingRequestSchema = z.object({
  messageId: z.string().uuid(),
  conversationId: z.string().uuid(),
  businessId: z.string().uuid(),
  text: z.string().min(1).max(32000),
});

export type EmbeddingRequest = z.infer<typeof EmbeddingRequestSchema>;

/**
 * Embedding generation result
 */
export const EmbeddingResultSchema = z.object({
  messageId: z.string().uuid(),
  embedding: z.array(z.number()).length(1536),
  model: z.string().default('text-embedding-3-small'),
  tokens: z.number().int().nonnegative(),
  duration: z.number().int().nonnegative(),
});

export type EmbeddingResult = z.infer<typeof EmbeddingResultSchema>;

/**
 * Importance scoring request
 */
export const ImportanceScoringRequestSchema = z.object({
  conversationId: z.string().uuid(),
  businessId: z.string().uuid(),
  messageId: z.string().uuid(),
  content: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  metadata: z.record(z.string(), z.any()).optional(),
});

export type ImportanceScoringRequest = z.infer<typeof ImportanceScoringRequestSchema>;

/**
 * Importance scoring result
 */
export const ImportanceScoringResultSchema = z.object({
  messageId: z.string().uuid(),
  importance: z.number().min(0).max(1),
  factors: z.object({
    isUserCorrection: z.boolean(),
    isAgentDecision: z.boolean(),
    hasToolResults: z.boolean(),
    tokenCost: z.number(),
    recencyBoost: z.number(),
  }),
  duration: z.number().int().nonnegative(),
});

export type ImportanceScoringResult = z.infer<typeof ImportanceScoringResultSchema>;

/**
 * Context window management request
 */
export const ContextWindowRequestSchema = z.object({
  conversationId: z.string().uuid(),
  businessId: z.string().uuid(),
  maxTokens: z.number().int().min(500).max(128000).default(4000),
  prioritizeImportance: z.boolean().default(true),
  includeSystemMessages: z.boolean().default(true),
});

export type ContextWindowRequest = z.infer<typeof ContextWindowRequestSchema>;

/**
 * Context window result with optimal message set
 */
export const ContextWindowResultSchema = z.object({
  conversationId: z.string().uuid(),
  messages: z.array(MessageRetrievalSchema),
  totalTokens: z.number().int().nonnegative(),
  summarizedSections: z.array(
    z.object({
      startIndex: z.number().int().nonnegative(),
      endIndex: z.number().int().nonnegative(),
      summary: z.string(),
      importance: z.number().min(0).max(1),
    }),
  ),
  computationDuration: z.number().int().nonnegative(),
});

export type ContextWindowResult = z.infer<typeof ContextWindowResultSchema>;
